import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import {
  LoginOptions,
  OAuthErrorEvent,
  OAuthLogger,
  OAuthService,
  OAuthSuccessEvent,
  TokenResponse,
} from 'angular-oauth2-oidc';
import { from, map, of, switchMap, tap } from 'rxjs';

/** Session key for the next Trimble Serial PKCE verifier (see Trimble ID PKCE guide). */
export const TRIMBLE_SERIAL_PKCE_VERIFIER_KEY = 'trimble_serial_pkce_verifier';

export interface TrimbleOAuthPatchTarget {
  disablePKCE: boolean;
  saveNoncesInLocalStorage: boolean;
  redirectUri?: string;
  tokenEndpoint?: string;
  clientId?: string;
  scope?: string;
  oidc: boolean;
  useHttpBasicAuth: boolean;
  dummyClientSecret?: string;
  customQueryParams?: Record<string, string>;
  fallbackAccessTokenExpirationTimeInSec: number;
  _storage: {
    getItem: (key: string) => string | null;
    setItem: (key: string, value: string) => void;
  };
  http: HttpClient;
  logger: OAuthLogger;
  debug: (...args: unknown[]) => void;
  assertUrlNotNullAndCorrectProtocol: (url: string | undefined, name: string) => void;
  createChallangeVerifierPairForPKCE: () => Promise<[string, string]>;
  fetchAndProcessToken: (params: HttpParams, options: LoginOptions) => Promise<void>;
  getTokenFromCode: (code: string, options?: LoginOptions) => Promise<void>;
  refreshToken: () => Promise<TokenResponse>;
  processIdToken: (
    idToken: string,
    accessToken: string,
    disableNonceCheck?: boolean
  ) => Promise<unknown>;
  storeIdToken: (idToken: unknown) => void;
  storeAccessTokenResponse: (
    accessToken: string,
    refreshToken: string,
    expiresIn: number,
    grantedScopes: string,
    customParameters?: Map<string, string>
  ) => void;
  extractRecognizedCustomParameters: (tokenResponse: TokenResponse) => Map<string, string>;
  eventsSubject: { next: (event: OAuthSuccessEvent | OAuthErrorEvent) => void };
}

/**
 * Trimble ID Authorization Code + PKCE requires Serial PKCE on token exchange and refresh.
 * Patches the shared OAuthService instance used by angular-oauth2-oidc.
 */
export function patchTrimbleSerialPkce(oauthService: OAuthService): void {
  const oauth = oauthService as unknown as TrimbleOAuthPatchTarget;

  oauth.getTokenFromCode = (code: string, options: LoginOptions = {}) => {
    const redirectUri = options.customRedirectUri ?? oauth.redirectUri ?? '';
    let params = new HttpParams()
      .set('grant_type', 'authorization_code')
      .set('code', code)
      .set('redirect_uri', redirectUri);

    if (!oauth.disablePKCE) {
      const pkceVerifier = readPkceVerifier(oauth);
      if (!pkceVerifier) {
        console.warn('No PKCE verifier found in oauth storage!');
      } else {
        params = params.set('code_verifier', pkceVerifier);
      }
    }

    return oauth.createChallangeVerifierPairForPKCE().then(([nextChallenge, nextVerifier]) => {
      if (!oauth.disablePKCE) {
        params = params
          .set('code_challenge', nextChallenge)
          .set('code_challenge_method', 'S256');
        storeSerialPkceVerifier(oauth, nextVerifier);
      }
      return oauth.fetchAndProcessToken(params, options);
    });
  };

  oauth.refreshToken = () => refreshTrimbleAccessToken(oauth);
}

export function refreshTrimbleAccessToken(oauth: TrimbleOAuthPatchTarget): Promise<TokenResponse> {
  oauth.assertUrlNotNullAndCorrectProtocol(oauth.tokenEndpoint, 'tokenEndpoint');

  const refreshToken = oauth._storage.getItem('refresh_token');
  if (!refreshToken) {
    return Promise.reject(new Error('No refresh token found'));
  }

  const serialVerifier = getSerialPkceVerifier(oauth);
  if (!serialVerifier) {
    return Promise.reject(
      new Error('No Trimble serial PKCE verifier found. Please sign in again.')
    );
  }

  const tokenEndpoint = oauth.tokenEndpoint ?? '';
  const clientId = oauth.clientId ?? '';
  const scope = oauth.scope ?? '';

  return oauth.createChallangeVerifierPairForPKCE().then(([nextChallenge, nextVerifier]) => {
    let params = new HttpParams()
      .set('grant_type', 'refresh_token')
      .set('scope', scope)
      .set('refresh_token', refreshToken)
      .set('client_id', clientId)
      .set('code_verifier', serialVerifier)
      .set('code_challenge', nextChallenge)
      .set('code_challenge_method', 'S256');

    let headers = new HttpHeaders().set('Content-Type', 'application/x-www-form-urlencoded');

    if (oauth.useHttpBasicAuth) {
      const header = btoa(`${clientId}:${oauth.dummyClientSecret ?? ''}`);
      headers = headers.set('Authorization', 'Basic ' + header);
    }

    if (!oauth.useHttpBasicAuth && oauth.dummyClientSecret) {
      params = params.set('client_secret', oauth.dummyClientSecret);
    }

    if (oauth.customQueryParams) {
      for (const key of Object.getOwnPropertyNames(oauth.customQueryParams)) {
        params = params.set(key, oauth.customQueryParams[key]);
      }
    }

    storeSerialPkceVerifier(oauth, nextVerifier);

    return new Promise<TokenResponse>((resolve, reject) => {
      oauth.http
        .post<TokenResponse>(tokenEndpoint, params, { headers })
        .pipe(
          switchMap((tokenResponse) => {
            if (oauth.oidc && tokenResponse.id_token) {
              return from(
                oauth.processIdToken(tokenResponse.id_token, tokenResponse.access_token, true)
              ).pipe(
                tap((result) => oauth.storeIdToken(result)),
                map(() => tokenResponse)
              );
            }
            return of(tokenResponse);
          })
        )
        .subscribe({
          next: (tokenResponse) => {
            oauth.debug('refresh tokenResponse', tokenResponse);
            oauth.storeAccessTokenResponse(
              tokenResponse.access_token,
              tokenResponse.refresh_token,
              tokenResponse.expires_in || oauth.fallbackAccessTokenExpirationTimeInSec,
              tokenResponse.scope,
              oauth.extractRecognizedCustomParameters(tokenResponse)
            );
            oauth.eventsSubject.next(new OAuthSuccessEvent('token_received'));
            oauth.eventsSubject.next(new OAuthSuccessEvent('token_refreshed'));
            resolve(tokenResponse);
          },
          error: (err: unknown) => {
            oauth.logger.error('Error refreshing token', err);
            oauth.eventsSubject.next(new OAuthErrorEvent('token_refresh_error', err as object));
            reject(err);
          },
        });
    });
  });
}

function readPkceVerifier(oauth: TrimbleOAuthPatchTarget): string | null {
  if (oauth.saveNoncesInLocalStorage && typeof window.localStorage !== 'undefined') {
    return localStorage.getItem('PKCE_verifier');
  }
  return oauth._storage.getItem('PKCE_verifier');
}

function storeSerialPkceVerifier(oauth: TrimbleOAuthPatchTarget, verifier: string): void {
  oauth._storage.setItem(TRIMBLE_SERIAL_PKCE_VERIFIER_KEY, verifier);
}

function getSerialPkceVerifier(oauth: TrimbleOAuthPatchTarget): string | null {
  return oauth._storage.getItem(TRIMBLE_SERIAL_PKCE_VERIFIER_KEY);
}
